import React from "react";
import { EventPolicyCompliance, EventPolicyComplianceDetail } from "common/graphql";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import formatDate from "date-fns/format";
import times from "lodash/times";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import { formatCurrency } from "common/format";
import type { Theme } from "@mui/material/styles";
import { formatRateUnit } from "common/format/rateUnit";

// Deliberately excludes incrementDuration: the $0.00 grace period is built from tiers that
// never agree on it (e.g. the first-minute tier carries 1, the next tier carries null), so
// comparing it here would block every real-world merge. Since both rows are already rate === 0
// at the call site, incrementDuration can't change the rendered price either way.
const appliedRatesMatch = (
  a: EventPolicyComplianceDetail["appliedRate"],
  b: EventPolicyComplianceDetail["appliedRate"],
) => a === b || (a !== null && b !== null && a.rate === b.rate && a.rateUnit === b.rateUnit);

/**
 * Displays the time and rate breakdown of a transaction to a payee user: per-period rate rows for a
 * park, or a single row for a flat charge.
 */
export const TransactionTimeAndRateBreakdown: React.FC<TransactionTimeAndRateBreakdownProps> = ({
  loading,
  policyCompliance = [],
  isFreeTransaction = false,
  flatCharge,
}) => {
  const policyComplianceDetails = policyCompliance
    .reduce(
      (acc, { details }) => [
        ...acc,
        ...details.map((d) => ({
          ...d,
          // At present, the start and end times of each rate description are cast
          // to an incorrect timezone. Correct this by adding an offset. Note this
          // will only work for US eastern time.
          // TODO: use new computed fields to populate these values
          rateStartAt: new Date(d.rateStartAt.replace("+00:00", "")).toISOString(),
          rateEndAt: new Date(d.rateEndAt.replace("+00:00", "")).toISOString(),
        })),
      ],
      [] as EventPolicyComplianceDetail[],
    )
    // policyCompliance entries are concatenated in whatever order the API returns them
    // (no order_by on the outer event_policy_compliance_records relation), so sort by
    // start time to make the contiguity check below meaningful.
    .sort((a, b) => new Date(a.rateStartAt).getTime() - new Date(b.rateStartAt).getTime());

  // The backend emits a separate detail row for every threshold crossing, even when
  // consecutive thresholds fall within the same $0.00 grace period (e.g. split at a
  // 1-minute boundary). Merge adjacent $0.00 rows into a single row spanning the
  // combined time range, but only when they're chronologically contiguous and describe
  // the same applied rate/violation status, so nothing distinct about the second row is
  // lost from the merged row.
  const consolidatedPolicyComplianceDetails = policyComplianceDetails.reduce((acc, pcd) => {
    const previous = acc[acc.length - 1];
    if (
      previous &&
      previous.rate === 0 &&
      pcd.rate === 0 &&
      previous.rateEndAt === pcd.rateStartAt &&
      previous.violation === pcd.violation &&
      appliedRatesMatch(previous.appliedRate, pcd.appliedRate)
    ) {
      return [...acc.slice(0, -1), { ...previous, rateEndAt: pcd.rateEndAt, price: previous.price + pcd.price }];
    }
    return [...acc, pcd];
  }, [] as EventPolicyComplianceDetail[]);

  const rateApplicationType = policyCompliance.length
    ? policyCompliance[0].policy.policyRules[0].rule.rateApplicationType
    : "additive";

  const isIncrementalRate = policyCompliance.some(({ details }) =>
    details.some(({ appliedRate }) => (appliedRate?.incrementDuration ?? 0) > 1),
  );

  return (
    <Paper>
      <Box sx={{ pt: 2.5, px: 3, pb: 1.5 }}>
        <Typography variant={"h5"} fontWeight={400}>
          Time and Rate Breakdown
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ height: 227, overflowY: loading ? "none" : "auto" }} mb={2} pr={2.5}>
        {loading ? (
          <LoadingContents />
        ) : flatCharge ? (
          <TimeAndRateRow
            timeLabel={flatCharge.chargedAt ? formatDate(flatCharge.chargedAt, "hh:mm aa") : "-"}
            rateLabel={
              isFreeTransaction
                ? "$0.00 (free)"
                : `$${formatCurrency(flatCharge.priceCents)} flat fee${flatCharge.voided ? " (voided)" : ""}`
            }
          />
        ) : (
          consolidatedPolicyComplianceDetails
            // In order to prevent display of rounded down rates where the final rate application results in a price of
            // zero dollars, we filter out those rate applications where the rate is non-zero and the price is zero.
            .filter((pcd) => pcd.rate === 0 || (pcd.rate !== 0 && pcd.price !== 0))
            .map((pcd) => {
              const timeLabel = `${formatDate(new Date(pcd.rateStartAt), "hh:mm aa")} - ${formatDate(
                new Date(pcd.rateEndAt),
                "hh:mm aa",
              )}`;

              const rateLabel = isFreeTransaction
                ? "$0.00 (free)"
                : pcd.violation
                ? "Violation"
                : isIncrementalRate
                ? (() => {
                    const { incrementDuration, rateUnit } = pcd.appliedRate ?? {};
                    return incrementDuration != null
                      ? `$${formatCurrency(pcd.rate * incrementDuration)} per ${incrementDuration} ${formatRateUnit(
                          rateUnit,
                        )}`
                      : `$${formatCurrency(pcd.rate)}`;
                  })()
                : `$${formatCurrency(pcd.rate)}${
                    rateApplicationType === "additive" ? ` per ${formatRateUnit(pcd.rateUnit)}` : ""
                  }`;

              return <TimeAndRateRow key={pcd.id} timeLabel={timeLabel} rateLabel={rateLabel} />;
            })
        )}
      </Box>
    </Paper>
  );
};

const LoadingContents: React.FC = () => (
  <Stack sx={{ p: 2.5 }} spacing={2.5}>
    {times(3, (i) => (
      <Box key={i} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
        <Skeleton width={221} height={50} />
        <Skeleton width={138} height={50} />
      </Box>
    ))}
  </Stack>
);

const TimeAndRateRow: React.FC<TimeAndRateRowProps> = ({ timeLabel, rateLabel }) => (
  <Box
    sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: (theme: Theme) => theme.palette.divider }}
    display={"flex"}
    flexDirection={"row"}
    justifyContent={"space-between"}
  >
    <Box>
      <Typography variant={"body1"} fontSize={20}>
        {timeLabel}
      </Typography>
    </Box>
    <Box>
      <Typography align="right" variant={"body1"} fontSize={20}>
        {rateLabel}
      </Typography>
    </Box>
  </Box>
);

interface TimeAndRateRowProps {
  timeLabel: string;
  rateLabel: string;
}

/** Props passed to initialize a {@link TransactionTimeAndRateBreakdown} component instance */
export interface TransactionTimeAndRateBreakdownProps {
  /** Indicates loading status of component */
  loading?: boolean;
  /** Policy compliance details indicating rate breakdown */
  policyCompliance?: EventPolicyCompliance[];
  isFreeTransaction?: boolean;
  /** A single flat charge (e.g. an airport trip charge); replaces the per-period policyCompliance rows */
  flatCharge?: { chargedAt?: Date; priceCents: number; voided?: boolean };
}

export default TransactionTimeAndRateBreakdown;
